import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Lock, Robot, Trophy, Target, Lightning, TrendUp } from '@phosphor-icons/react';

export default function Signals() {
  const { user } = useAuth();
  const [signals, setSignals] = useState([]);
  const [perf, setPerf] = useState({});
  const [tab, setTab] = useState('ALL');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset: 'BTCUSDT', direction: 'BUY', entry: '', stop_loss: '', take_profit_1: '', take_profit_2: '', take_profit_3: '', risk_percent: 1, analysis: '', tradingview_link: '' });

  const load = async () => {
    const q = tab === 'ALL' ? '' : `?status_filter=${tab}`;
    const [s, p] = await Promise.all([api.get(`/signals${q}`), api.get('/signals/performance')]);
    setSignals(s.data); setPerf(p.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab]);

  const publish = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, entry: parseFloat(form.entry), stop_loss: parseFloat(form.stop_loss),
        take_profit_1: parseFloat(form.take_profit_1),
        take_profit_2: form.take_profit_2 ? parseFloat(form.take_profit_2) : null,
        take_profit_3: form.take_profit_3 ? parseFloat(form.take_profit_3) : null,
        risk_percent: parseFloat(form.risk_percent) };
      await api.post('/signals', payload);
      toast.success('Signal published & broadcast');
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/signals/${id}`, { status });
    load();
  };

  return (
    <div className="space-y-6" data-testid="signals-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="data-label text-saffron">Live Feed</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Trading Signals</h1>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="publish-signal-btn" className="bg-saffron hover:bg-saffron-hover text-white shadow-glow-saffron">
                <Plus size={16} className="mr-2" weight="bold" /> Publish Signal
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-strong border-white/10 max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Publish New Signal</DialogTitle></DialogHeader>
              <form onSubmit={publish} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="data-label">Asset</Label><Input data-testid="sig-asset" required value={form.asset} onChange={e => setForm({...form, asset: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                  <div><Label className="data-label">Direction</Label>
                    <Select value={form.direction} onValueChange={v => setForm({...form, direction: v})}>
                      <SelectTrigger className="mt-1 bg-surface-base border-white/10" data-testid="sig-direction"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="data-label">Entry</Label><Input data-testid="sig-entry" type="number" step="any" required value={form.entry} onChange={e => setForm({...form, entry: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                  <div><Label className="data-label">SL</Label><Input data-testid="sig-sl" type="number" step="any" required value={form.stop_loss} onChange={e => setForm({...form, stop_loss: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                  <div><Label className="data-label">Risk %</Label><Input type="number" step="any" required value={form.risk_percent} onChange={e => setForm({...form, risk_percent: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="data-label">TP1</Label><Input data-testid="sig-tp1" type="number" step="any" required value={form.take_profit_1} onChange={e => setForm({...form, take_profit_1: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                  <div><Label className="data-label">TP2</Label><Input type="number" step="any" value={form.take_profit_2} onChange={e => setForm({...form, take_profit_2: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                  <div><Label className="data-label">TP3</Label><Input type="number" step="any" value={form.take_profit_3} onChange={e => setForm({...form, take_profit_3: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
                </div>
                <div><Label className="data-label">Analysis</Label><Textarea value={form.analysis} onChange={e => setForm({...form, analysis: e.target.value})} className="mt-1 bg-surface-base border-white/10" rows={3} /></div>
                <div><Label className="data-label">TradingView Link</Label><Input value={form.tradingview_link} onChange={e => setForm({...form, tradingview_link: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="https://www.tradingview.com/x/..." /></div>
                <Button type="submit" data-testid="sig-submit" className="w-full bg-saffron hover:bg-saffron-hover text-white">Publish & Broadcast</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          {l:'Total', v: perf.total, i: TrendUp, c: 'text-white'},
          {l:'Open', v: perf.open, i: Lightning, c: 'text-blue-400'},
          {l:'Win Rate', v: (perf.win_rate ?? 0)+'%', i: Trophy, c: 'text-green-400'},
          {l:'Avg RR', v: '1:'+(perf.avg_rr ?? 0), i: Target, c: 'text-saffron'},
          {l:'TP1 Hit', v: perf.tp1_hit, i: Target, c: 'text-green-400'},
          {l:'TP3 Hit', v: perf.tp3_hit, i: Trophy, c: 'text-green-400'},
        ].map(({l,v,i:I,c}) => (
          <div key={l} className="glass p-3 rounded-lg neon-border">
            <div className="flex items-center gap-2"><I size={16} className={c} weight="duotone" /><div className="data-label">{l}</div></div>
            <div className={`stat-num text-2xl mt-1 ${c}`}>{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        {['ALL','OPEN','CLOSED_WIN','CLOSED_LOSS'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`filter-${t}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t ? 'bg-saffron text-white' : 'glass hover:bg-white/10'}`}>
            {t.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="grid gap-3">
        {signals.length === 0 && <div className="glass p-8 rounded-lg text-center text-muted-foreground">No signals in this view yet.</div>}
        {signals.map(s => (
          <div key={s.id} className="glass p-5 rounded-lg neon-border" data-testid={`signal-${s.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center font-bold text-white ${s.direction === 'BUY' ? 'bg-green-500/20 border border-green-500/40 text-green-400' : 'bg-red-500/20 border border-red-500/40 text-red-400'}`}>
                  {s.direction}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold text-xl font-mono">{s.asset}</div>
                    {s.source === 'auto_engine' && (
                      <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-saffron/15 text-saffron uppercase tracking-widest font-bold">
                        <Robot size={11} weight="fill" /> AI
                      </span>
                    )}
                    {typeof s.confidence_score === 'number' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-mono">
                        {s.confidence_score}% conf · 1:{s.risk_reward}
                      </span>
                    )}
                    {s.timeframe && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-muted-foreground">{s.timeframe}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{new Date(s.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {s.milestones?.tp1 && <span className="text-[10px] px-2 py-1 rounded bg-green-500/15 text-green-400 font-bold">TP1 ✓</span>}
                {s.milestones?.tp2 && <span className="text-[10px] px-2 py-1 rounded bg-green-500/15 text-green-400 font-bold">TP2 ✓</span>}
                {s.milestones?.tp3 && <span className="text-[10px] px-2 py-1 rounded bg-green-500/15 text-green-400 font-bold">TP3 ✓</span>}
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${s.status === 'OPEN' ? 'bg-blue-500/15 text-blue-400' : s.status === 'CLOSED_WIN' ? 'bg-green-500/15 text-green-400' : s.status === 'CLOSED_LOSS' ? 'bg-red-500/15 text-red-400' : 'bg-muted/20 text-muted-foreground'}`}>
                  {s.status === 'OPEN' ? 'RUNNING' : s.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            {s.locked ? (
              <div className="mt-4 p-4 bg-surface-base rounded-md text-center">
                <Lock size={24} className="text-saffron mx-auto mb-2" weight="duotone" />
                <div className="text-sm text-muted-foreground">Upgrade to Premium to unlock full signal details.</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                <div><div className="data-label">Entry</div><div className="font-mono font-bold mt-1">{s.entry}</div></div>
                <div><div className="data-label">SL</div><div className="font-mono font-bold mt-1 text-red-400">{s.stop_loss}</div></div>
                <div><div className="data-label">TP1</div><div className="font-mono font-bold mt-1 text-green-400">{s.take_profit_1}</div></div>
                {s.take_profit_2 && <div><div className="data-label">TP2</div><div className="font-mono font-bold mt-1 text-green-400">{s.take_profit_2}</div></div>}
                {s.take_profit_3 && <div><div className="data-label">TP3</div><div className="font-mono font-bold mt-1 text-green-400">{s.take_profit_3}</div></div>}
              </div>
            )}

            {!s.locked && s.analysis && <p className="mt-4 text-sm text-muted-foreground">{s.analysis}</p>}

            {user?.role === 'admin' && (
              <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-white/5">
                <Button size="sm" variant="outline" onClick={() => setStatus(s.id, 'CLOSED_WIN')} className="border-green-500/30 text-green-400 hover:bg-green-500/10">Mark WIN</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(s.id, 'CLOSED_LOSS')} className="border-red-500/30 text-red-400 hover:bg-red-500/10">Mark LOSS</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(s.id, 'CANCELLED')} className="border-white/10">Cancel</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
